// scenarioIO.ts — JSON export/import of a single ScenarioState (Szenario-Bibliothek,
// Feature 3). This is the ONE shared validator for both the file-download/upload path
// (SzenarienView) and the shareable-link path (lib/share.ts encodes/decodes the exact
// same JSON shape into a URL-safe string) — a shared link is just an export whose bytes
// happen to live in the URL instead of a downloaded file. Reuses storage.ts's
// mergeScenario (the same field-by-field defaulting logic loadPersistedState uses for a
// localStorage migration) so an older/partial export still loads instead of being
// rejected outright — but a genuinely malformed/oversized blob IS rejected with an
// honest error, never silently coerced and never a crash.
import { createScenario } from './scenario.js';
import type { ScenarioState } from './scenario.js';
import { mergeScenario } from './storage.js';

export const SCENARIO_EXPORT_VERSION = 1;

export interface ScenarioExport {
  version: number;
  scenario: ScenarioState;
}

export type ScenarioImportResult = { ok: true; scenario: ScenarioState } | { ok: false; error: string };

/** Generous but sane cap on the raw import string — a real scenario export is a few KB;
 * this only guards against something absurd (a wrong file, a pasted novel) rather than
 * being a tight budget. */
export const MAX_IMPORT_LENGTH = 200_000;

export function serializeScenario(scenario: ScenarioState): string {
  const payload: ScenarioExport = { version: SCENARIO_EXPORT_VERSION, scenario };
  return JSON.stringify(payload, null, 2);
}

function isPlainObject(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

/** The "shape gate": rejects anything that isn't at least plausibly a ScenarioState
 * (mode + forecast + backtest all present with the right basic types) BEFORE handing it
 * to mergeScenario's field-by-field defaulting — mergeScenario is deliberately forgiving
 * about individual missing fields (that's what makes old exports/migrations work), but it
 * must never be asked to "default" an object that isn't a scenario at all. */
function looksLikeScenario(x: unknown): x is Partial<ScenarioState> {
  if (!isPlainObject(x)) return false;
  const mode = x['mode'];
  if (mode !== 'forecast' && mode !== 'backtest') return false;
  return isPlainObject(x['forecast']) && isPlainObject(x['backtest']);
}

/**
 * Validates + parses a raw string (pasted or uploaded JSON, or a decoded share-link
 * payload) into a ScenarioState. Accepts either the wrapped export shape
 * (`{version, scenario}`, what serializeScenario produces) or a bare ScenarioState
 * object, so a hand-edited or directly-copied scenario blob still works. Never throws:
 * every failure path returns `{ok: false, error}` with an honest, specific message.
 */
export function deserializeScenario(raw: string): ScenarioImportResult {
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return { ok: false, error: 'Leere Eingabe.' };
  }
  if (raw.length > MAX_IMPORT_LENGTH) {
    return { ok: false, error: `Eingabe zu groß (${raw.length} Zeichen, Limit ${MAX_IMPORT_LENGTH}).` };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, error: 'Ungültiges JSON — die Datei/der Text konnte nicht gelesen werden.' };
  }

  if (!isPlainObject(parsed)) {
    return { ok: false, error: 'Unbekanntes Format: kein JSON-Objekt.' };
  }

  const candidate = isPlainObject(parsed['scenario']) ? parsed['scenario'] : parsed;
  if (!looksLikeScenario(candidate)) {
    return {
      ok: false,
      error: 'Unbekanntes Format: keine gültige Szenario-Struktur (mode/forecast/backtest fehlen oder sind ungültig).',
    };
  }

  const label = typeof candidate['label'] === 'string' ? (candidate['label'] as string) : 'Importiertes Szenario';
  const fallback = createScenario(label);
  const scenario = mergeScenario(candidate, fallback);
  return { ok: true, scenario };
}
